import React, { useState, useEffect } from 'react';
import {
    Paper,
    TableContainer,
    TableHead,
    TableCell,
    Table,
    Box,
    Typography,
    TableBody,
    TableRow,
    Chip,
    TableFooter,
    TablePagination,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    Tooltip,
    Divider,
    Switch,
    FormControlLabel,
    TextField,
    InputAdornment,
    Checkbox,
    Toolbar,
    Alert,
    CircularProgress,
    Fab,
    Zoom,
    Badge,
    Stack,
    useMediaQuery,
    Collapse
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import API from '../../../../../API';
import useIsDesktop from '../../../../../Hooks/useIsDesktop';
import { StyledTableRow } from '../../../../../Utils/styledcell';
import {
    Email,
    Refresh,
    Search,
    Visibility,
    VisibilityOff,
    CheckBox,
    CheckBoxOutlineBlank,
    IndeterminateCheckBox,
    Delete,
    RestoreFromTrash,
    MoreVert,
    Reply as ReplyIcon,
    AccountTree as ThreadIcon,
    CheckCircle as CheckCircleIcon,
    Forward as ForwardIcon,
    ExpandMore,
    ExpandLess
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';

// Styled components
const HideMobileCell = styled(TableCell)(({ theme }) => ({
    display: 'none',
    [theme.breakpoints.up('md')]: {
        display: 'table-cell',
    },
}));

const HideTabletCell = styled(TableCell)(({ theme }) => ({
    display: 'none',
    [theme.breakpoints.up('lg')]: {
        display: 'table-cell',
    },
}));

const SelectionToolbar = styled(Toolbar)(({ theme }) => ({
    position: 'sticky',
    top: 0,
    zIndex: 1100,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    minHeight: '64px',
    boxShadow: theme.shadows[4],
}));

const FloatingActionBar = styled(Box)(({ theme }) => ({
    position: 'fixed',
    bottom: 100,
    right: theme.spacing(3),
    zIndex: 1100,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
    maxHeight: 'calc(100vh - 400px)',
    overflow: 'auto',
    '& .MuiTable-root': {
        tableLayout: 'fixed',
        width: '100%',
    },
    [theme.breakpoints.down('md')]: {
        maxHeight: 'calc(100vh - 300px)',
    },
}));

const TruncatedText = styled(Typography)(({ theme }) => ({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
}));

const rowsPerPage = 15;

// Email stage color mapping
const STAGE_COLORS = {
    'received': 'default',
    'replied': 'success',
    'followup_sent': 'secondary',
    'cc_manager': 'warning',
    'closed': 'default'
};

export default function EmailHistoryTab({ 
    keywords, 
    onEmailClick, 
    showToast,
    isMobile,
    isTablet 
}) {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
    const isLargeScreen = useMediaQuery(theme.breakpoints.down('lg'));
    
    const [page, setPage] = useState(0);
    const [selectedCreator, setSelectedCreator] = useState('');
    const [sortOrder, setSortOrder] = useState('desc');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [emailSearchQuery, setEmailSearchQuery] = useState('');
    const [appliedEmailSearch, setAppliedEmailSearch] = useState('');
    const [creatorIds, setCreatorIds] = useState([]);
    const [appliedFilters, setAppliedFilters] = useState({
        creatorId: '',
        sortOrder: 'desc',
        category: ''
    });
    const [showHidden, setShowHidden] = useState(false);
    const [hiddenCount, setHiddenCount] = useState(0);
    
    const [selectedEmails, setSelectedEmails] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [bulkOperationInProgress, setBulkOperationInProgress] = useState(false);
    
    const [filtersExpanded, setFiltersExpanded] = useState(!isSmallScreen);
    
    const isDesktop = useIsDesktop();
    const queryClient = useQueryClient();

    const { data: emails, error: emailsError, isFetching: fetchingEmails } = useQuery({
        queryKey: ['creatorEmails', page, appliedFilters.creatorId, appliedFilters.sortOrder, appliedFilters.category, appliedEmailSearch, showHidden],
        queryFn: () => API.creators.adminBrandEmailsList({ 
            page, 
            rows: rowsPerPage,
            creator_id: appliedFilters.creatorId || undefined,
            sort_order: appliedFilters.sortOrder,
            category: appliedFilters.category || undefined,
            search: appliedEmailSearch || undefined,
            show_hidden: showHidden
        }),
        refetchInterval: false,
        refetchOnMount: 'always',
        refetchOnWindowFocus: false,
        enabled: !!keywords,
        onSuccess: (data) => {
            if (data && data.hidden_count !== undefined) {
                setHiddenCount(data.hidden_count);
            }
            setSelectedEmails([]);
            setSelectAll(false);
        }
    });

    const toggleHiddenMutation = useMutation(
        ({ emailId, hidden }) => API.blitzAI.toggleEmailHidden(emailId, hidden),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['creatorEmails']);
                showToast('Email visibility updated', 'success');
            },
            onError: (error) => {
                showToast(error.response?.data?.error || 'Failed to update email visibility');
            }
        }
    );

    const bulkHideMutation = useMutation(
        ({ emailIds, hidden }) => {
            return Promise.all(
                emailIds.map(emailId => 
                    API.blitzAI.toggleEmailHidden(emailId, hidden)
                )
            );
        },
        {
            onMutate: () => {
                setBulkOperationInProgress(true);
            },
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries(['creatorEmails']);
                showToast(
                    `Successfully ${variables.hidden ? 'archived' : 'restored'} ${variables.emailIds.length} email(s)`, 
                    'success'
                );
                setSelectedEmails([]);
                setSelectAll(false);
            },
            onError: (error) => {
                showToast(error.response?.data?.error || 'Failed to update email visibility');
            },
            onSettled: () => {
                setBulkOperationInProgress(false);
            }
        }
    );

    useEffect(() => {
        if (emails && emails.emails) {
            const uniqueCreatorIds = [...new Set(emails.emails.map(email => email.creator_id).filter(Boolean))];
            setCreatorIds(uniqueCreatorIds);
        }
    }, [emails]);

    useEffect(() => {
        if (!!emailsError) {
            console.error('An error occurred:', emailsError.message);
            showToast(emailsError.response?.data?.error || emailsError.message);
        }
    }, [emailsError]);

    const getCategoryStats = () => {
        if (!emails?.emails) return {};
        
        const stats = {};
        emails.emails.forEach(email => {
            if (email.category) {
                stats[email.category] = (stats[email.category] || 0) + 1;
            }
        });
        return stats;
    };

    const getFlowStats = () => {
        if (!emails?.emails) return { totalReplies: 0, threadsWithReplies: 0, avgRepliesPerThread: 0 };
        
        let totalReplies = 0;
        let threadsWithReplies = 0;
        
        emails.emails.forEach(email => {
            const replyCount = email.our_reply_count || 0;
            if (replyCount > 0) {
                totalReplies += replyCount;
                threadsWithReplies++;
            }
        });
        
        return {
            totalReplies,
            threadsWithReplies,
            avgRepliesPerThread: threadsWithReplies > 0 ? (totalReplies / threadsWithReplies).toFixed(1) : 0
        };
    };

    const categoryStats = getCategoryStats();
    const flowStats = getFlowStats();
    const totalEmails = emails?.total || 0;
    const uniqueCreators = creatorIds.length;

    const handleApplyFilters = () => {
        setAppliedFilters({
            creatorId: selectedCreator,
            sortOrder: sortOrder,
            category: categoryFilter
        });
        setAppliedEmailSearch(emailSearchQuery);
        setPage(0);
        setSelectedEmails([]);
        setSelectAll(false);
    };

    const handleResetEmailFilters = () => {
        setSelectedCreator('');
        setSortOrder('desc');
        setCategoryFilter('');
        setEmailSearchQuery('');
        setAppliedEmailSearch('');
        setShowHidden(false);
        setAppliedFilters({
            creatorId: '',
            sortOrder: 'desc',
            category: ''
        });
        setPage(0);
        setSelectedEmails([]);
        setSelectAll(false);
    };

    const handleQuickFilter = (category) => {
        setCategoryFilter(category);
        setAppliedFilters(prev => ({
            ...prev,
            category: category
        }));
        setPage(0);
        setSelectedEmails([]);
        setSelectAll(false);
    };

    const handleShowHiddenToggle = (event) => {
        setShowHidden(event.target.checked);
        setPage(0);
        setSelectedEmails([]);
        setSelectAll(false);
    };

    const handleToggleEmailVisibility = (event, emailId, currentHidden) => {
        event.stopPropagation();
        toggleHiddenMutation.mutate({ emailId, hidden: !currentHidden });
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const allEmailIds = emails.emails.map(email => email.id);
            setSelectedEmails(allEmailIds);
            setSelectAll(true);
        } else {
            setSelectedEmails([]);
            setSelectAll(false);
        }
    };

    const handleSelectEmail = (event, emailId) => {
        event.stopPropagation();
        
        if (selectedEmails.includes(emailId)) {
            setSelectedEmails(selectedEmails.filter(id => id !== emailId));
            setSelectAll(false);
        } else {
            const newSelected = [...selectedEmails, emailId];
            setSelectedEmails(newSelected);
            setSelectAll(newSelected.length === emails.emails.length);
        }
    };

    const handleBulkHide = () => {
        if (selectedEmails.length === 0) {
            showToast('No emails selected', 'warning');
            return;
        }

        bulkHideMutation.mutate({
            emailIds: selectedEmails,
            hidden: true
        });
    };

    const handleBulkUnhide = () => {
        if (selectedEmails.length === 0) {
            showToast('No emails selected', 'warning');
            return;
        }

        bulkHideMutation.mutate({
            emailIds: selectedEmails,
            hidden: false
        });
    };

    const handleClearSelection = () => {
        setSelectedEmails([]);
        setSelectAll(false);
    };

    const isEmailSelected = (emailId) => selectedEmails.includes(emailId);
    const hasSelection = selectedEmails.length > 0;

    const formatStage = (stage) => {
        const stages = {
            'received': 'Received',
            'replied': 'Replied',
            'followup_sent': 'Follow-up',
            'cc_manager': 'Manager CC\'d',
            'closed': 'Closed'
        };
        return stages[stage] || stage;
    };

    // Mobile Card View Component for individual emails
    const MobileEmailCard = ({ email }) => {
        const isSelected = isEmailSelected(email.id);
        const replyCount = email.our_reply_count || 0;
        const emailStage = email.email_stage || 'received';
        
        return (
            <Card 
                sx={{ 
                    mb: 2, 
                    cursor: 'pointer',
                    opacity: email.admin_hidden ? 0.6 : 1,
                    backgroundColor: email.admin_hidden 
                        ? 'action.disabledBackground' 
                        : isSelected 
                            ? 'action.selected' 
                            : 'inherit',
                    border: isSelected ? 2 : 1,
                    borderColor: isSelected ? 'primary.main' : 'divider'
                }}
                onClick={() => onEmailClick(email.id)}
            >
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Checkbox
                            checked={isSelected}
                            onChange={(e) => handleSelectEmail(e, email.id)}
                            onClick={(e) => e.stopPropagation()}
                        />
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {email.category && (
                                <Chip 
                                    label={email.category} 
                                    size="small"
                                    color={email.category === 'Paid' ? 'success' : email.category === 'Affiliate' ? 'secondary' : 'default'}
                                />
                            )}
                            <Chip
                                label={formatStage(emailStage)}
                                size="small"
                                color={STAGE_COLORS[emailStage]}
                            />
                        </Stack>
                    </Box>
                    
                    <Typography variant="subtitle2" fontWeight="600" gutterBottom noWrap>
                        {email.subject}
                    </Typography>
                    
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        From: <strong>{email.sender}</strong>
                    </Typography>
                    
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        To: {email.receiver}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {email.snippet}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                            {new Date(email.date_received).toLocaleDateString()} {new Date(email.date_received).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            {replyCount > 0 && (
                                <Chip
                                    icon={<ReplyIcon sx={{ fontSize: 14 }} />}
                                    label={replyCount}
                                    size="small"
                                    color="success"
                                    sx={{ height: 20 }}
                                />
                            )}
                            
                            {email.admin_hidden ? (
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => handleToggleEmailVisibility(e, email.id, email.admin_hidden)}
                                >
                                    <RestoreFromTrash fontSize="small" />
                                </IconButton>
                            ) : (
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => handleToggleEmailVisibility(e, email.id, email.admin_hidden)}
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        );
    };

    return (
        <Box>
            {hasSelection && (
                <SelectionToolbar>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 2 }}>
                        <Typography variant={isSmallScreen ? "body1" : "h6"}>
                            {selectedEmails.length} selected
                        </Typography>
                        
                        {bulkOperationInProgress && (
                            <CircularProgress size={24} color="inherit" />
                        )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {!isSmallScreen && (
                            <>
                                <Button
                                    variant="contained"
                                    color="inherit"
                                    startIcon={<Delete />}
                                    onClick={handleBulkHide}
                                    disabled={bulkOperationInProgress}
                                >
                                    Archive
                                </Button>
                                <Button
                                    variant="contained"
                                    color="inherit"
                                    startIcon={<RestoreFromTrash />}
                                    onClick={handleBulkUnhide}
                                    disabled={bulkOperationInProgress}
                                >
                                    Restore
                                </Button>
                            </>
                        )}
                        <Button
                            variant="outlined"
                            color="inherit"
                            onClick={handleClearSelection}
                            disabled={bulkOperationInProgress}
                            size={isSmallScreen ? "small" : "medium"}
                        >
                            Clear
                        </Button>
                    </Box>
                </SelectionToolbar>
            )}

            {hasSelection && isSmallScreen && (
                <FloatingActionBar>
                    <Tooltip title="Archive Selected" placement="left">
                        <Fab
                            color="error"
                            size="medium"
                            onClick={handleBulkHide}
                            disabled={bulkOperationInProgress}
                        >
                            <Badge badgeContent={selectedEmails.length} color="secondary">
                                <Delete />
                            </Badge>
                        </Fab>
                    </Tooltip>
                    <Tooltip title="Restore Selected" placement="left">
                        <Fab
                            color="primary"
                            size="medium"
                            onClick={handleBulkUnhide}
                            disabled={bulkOperationInProgress}
                        >
                            <Badge badgeContent={selectedEmails.length} color="secondary">
                                <RestoreFromTrash />
                            </Badge>
                        </Fab>
                    </Tooltip>
                </FloatingActionBar>
            )}

            {/* Stats Cards - Responsive Grid */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={6} md={3}>
                    <Card>
                        <CardContent sx={{ py: isSmallScreen ? 1.5 : 2 }}>
                            <Typography color="text.secondary" gutterBottom variant="caption">
                                Total Emails
                            </Typography>
                            <Typography variant={isSmallScreen ? "h5" : "h4"}>{totalEmails}</Typography>
                            {!isSmallScreen && (
                                <Typography variant="caption" color="text.secondary">
                                    Across all creators
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                    <Card>
                        <CardContent sx={{ py: isSmallScreen ? 1.5 : 2 }}>
                            <Typography color="text.secondary" gutterBottom variant="caption">
                                Active Creators
                            </Typography>
                            <Typography variant={isSmallScreen ? "h5" : "h4"} color="primary.main">{uniqueCreators}</Typography>
                            {!isSmallScreen && (
                                <Typography variant="caption" color="text.secondary">
                                    With email history
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                    <Card>
                        <CardContent sx={{ py: isSmallScreen ? 1.5 : 2 }}>
                            <Typography color="text.secondary" gutterBottom variant="caption">
                                Paid Deals
                            </Typography>
                            <Typography variant={isSmallScreen ? "h5" : "h4"} color="success.main">
                                {categoryStats['Paid'] || 0}
                            </Typography>
                            {!isSmallScreen && (
                                <Typography variant="caption" color="text.secondary">
                                    Opportunities
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                    <Card>
                        <CardContent sx={{ py: isSmallScreen ? 1.5 : 2 }}>
                            <Typography color="text.secondary" gutterBottom variant="caption">
                                Auto Replies
                            </Typography>
                            <Typography variant={isSmallScreen ? "h5" : "h4"} color="success.main">
                                {flowStats.totalReplies}
                            </Typography>
                            {!isSmallScreen && (
                                <Typography variant="caption" color="text.secondary">
                                    {flowStats.threadsWithReplies} threads
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters Card - Collapsible on Mobile */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: filtersExpanded ? 2 : 0 }}>
                        <Typography variant="h6">
                            {isSmallScreen ? 'Filters' : 'Filter & Search'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {isSmallScreen && (
                                <IconButton 
                                    size="small"
                                    onClick={() => setFiltersExpanded(!filtersExpanded)}
                                >
                                    {filtersExpanded ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                            )}
                            {filtersExpanded && (
                                <>
                                    <Button
                                        size="small"
                                        startIcon={!isSmallScreen && <Refresh />}
                                        onClick={handleResetEmailFilters}
                                    >
                                        {isSmallScreen ? 'Reset' : 'Reset All'}
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={handleApplyFilters}
                                    >
                                        Apply
                                    </Button>
                                </>
                            )}
                        </Box>
                    </Box>

                    <Collapse in={filtersExpanded}>
                        {/* Quick Category Filters */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                Quick Filters:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button
                                    size="small"
                                    variant={categoryFilter === 'Paid' ? 'contained' : 'outlined'}
                                    color="success"
                                    onClick={() => handleQuickFilter('Paid')}
                                >
                                    Paid ({categoryStats['Paid'] || 0})
                                </Button>
                                <Button
                                    size="small"
                                    variant={categoryFilter === 'Affiliate' ? 'contained' : 'outlined'}
                                    color="secondary"
                                    onClick={() => handleQuickFilter('Affiliate')}
                                >
                                    Affiliate ({categoryStats['Affiliate'] || 0})
                                </Button>
                                <Button
                                    size="small"
                                    variant={categoryFilter === 'Agency' ? 'contained' : 'outlined'}
                                    color="info"
                                    onClick={() => handleQuickFilter('Agency')}
                                >
                                    Agency ({categoryStats['Agency'] || 0})
                                </Button>
                                {!isSmallScreen && (
                                    <Button
                                        size="small"
                                        variant={categoryFilter === 'Negotiating' ? 'contained' : 'outlined'}
                                        color="warning"
                                        onClick={() => handleQuickFilter('Negotiating')}
                                    >
                                        Negotiating ({categoryStats['Negotiating'] || 0})
                                    </Button>
                                )}
                            </Box>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {/* Search and Filter Controls */}
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Search"
                                    placeholder="Sender, subject..."
                                    value={emailSearchQuery}
                                    onChange={(e) => setEmailSearchQuery(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') handleApplyFilters();
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={4} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Creator</InputLabel>
                                    <Select
                                        value={selectedCreator}
                                        label="Creator"
                                        onChange={(e) => setSelectedCreator(e.target.value)}
                                    >
                                        <MenuItem value="">
                                            <em>All</em>
                                        </MenuItem>
                                        {creatorIds.map((id) => (
                                            <MenuItem key={id} value={id}>{id}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={4} md={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Category</InputLabel>
                                    <Select
                                        value={categoryFilter}
                                        label="Category"
                                        onChange={(e) => setCategoryFilter(e.target.value)}
                                    >
                                        <MenuItem value="">
                                            <em>All</em>
                                        </MenuItem>
                                        {Object.keys(categoryStats).sort().map((cat) => (
                                            <MenuItem key={cat} value={cat}>
                                                {cat}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={4} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Sort</InputLabel>
                                    <Select
                                        value={sortOrder}
                                        label="Sort"
                                        onChange={(e) => setSortOrder(e.target.value)}
                                    >
                                        <MenuItem value="desc">Newest First</MenuItem>
                                        <MenuItem value="asc">Oldest First</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        {/* Hidden Emails Alert */}
                        {hiddenCount > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Alert severity="info" icon={<RestoreFromTrash />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                        <Typography variant="body2">
                                            {hiddenCount} archived
                                        </Typography>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={showHidden}
                                                    onChange={handleShowHiddenToggle}
                                                    size="small"
                                                />
                                            }
                                            label="Show"
                                        />
                                    </Box>
                                </Alert>
                            </Box>
                        )}

                        {/* Active Filters Chips */}
                        {(appliedEmailSearch || appliedFilters.creatorId || appliedFilters.category || showHidden) && (
                            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary">
                                    Active:
                                </Typography>
                                {appliedEmailSearch && (
                                    <Chip
                                        size="small"
                                        label={`"${appliedEmailSearch.substring(0, 20)}${appliedEmailSearch.length > 20 ? '...' : ''}"`}
                                        onDelete={() => {
                                            setEmailSearchQuery('');
                                            setAppliedEmailSearch('');
                                            setPage(0);
                                        }}
                                    />
                                )}
                                {appliedFilters.creatorId && (
                                    <Chip
                                        size="small"
                                        label={appliedFilters.creatorId}
                                        onDelete={() => {
                                            setSelectedCreator('');
                                            setAppliedFilters(prev => ({ ...prev, creatorId: '' }));
                                            setPage(0);
                                        }}
                                    />
                                )}
                                {appliedFilters.category && (
                                    <Chip
                                        size="small"
                                        label={appliedFilters.category}
                                        onDelete={() => {
                                            setCategoryFilter('');
                                            setAppliedFilters(prev => ({ ...prev, category: '' }));
                                            setPage(0);
                                        }}
                                    />
                                )}
                                {showHidden && (
                                    <Chip
                                        size="small"
                                        color="warning"
                                        label="Archived"
                                        onDelete={() => {
                                            setShowHidden(false);
                                            setPage(0);
                                        }}
                                    />
                                )}
                            </Box>
                        )}

                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                {emails ? `${emails.emails.length} of ${emails.total}` : 'Loading...'}
                            </Typography>
                            {hasSelection && (
                                <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                                    {selectedEmails.length} selected
                                </Typography>
                            )}
                        </Box>
                    </Collapse>
                </CardContent>
            </Card>

            {/* Mobile Card View */}
            {isSmallScreen ? (
                <Box>
                    {!emails || emails.emails.length === 0 ? (
                        <Card>
                            <CardContent>
                                <Box sx={{ py: 4, textAlign: 'center' }}>
                                    <Email sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                    <Typography variant="h6" color="text.secondary" gutterBottom>
                                        No emails found
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {appliedEmailSearch || appliedFilters.creatorId || appliedFilters.category
                                            ? 'Try adjusting filters'
                                            : 'Emails will appear here'}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    ) : (
                        <>
                            {emails.emails.map((email) => (
                                <MobileEmailCard key={email.id} email={email} />
                            ))}
                            
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <TablePagination
                                    component="div"
                                    page={page}
                                    rowsPerPage={rowsPerPage}
                                    rowsPerPageOptions={[rowsPerPage]}
                                    count={emails?.total || 0}
                                    onPageChange={(event, newPage) => {
                                        setPage(newPage);
                                        setSelectedEmails([]);
                                        setSelectAll(false);
                                    }}
                                />
                            </Box>
                        </>
                    )}
                </Box>
            ) : (
                /* Desktop Table View */
                <Card elevation={1}>
                    <ResponsiveTableContainer>
                        <Table stickyHeader>
                            <TableHead>
                                <StyledTableRow>
                                    <TableCell padding="checkbox" sx={{ width: '50px' }}>
                                        <Checkbox
                                            indeterminate={hasSelection && !selectAll}
                                            checked={selectAll}
                                            onChange={handleSelectAll}
                                            disabled={!emails || emails.emails.length === 0}
                                            icon={<CheckBoxOutlineBlank />}
                                            checkedIcon={<CheckBox />}
                                            indeterminateIcon={<IndeterminateCheckBox />}
                                        />
                                    </TableCell>
                                    <HideTabletCell sx={{ fontWeight: 600, width: '10%' }}>
                                        Creator
                                    </HideTabletCell>
                                    <TableCell sx={{ fontWeight: 600, width: '12%' }}>
                                        Sender
                                    </TableCell>
                                    <HideMobileCell sx={{ fontWeight: 600, width: '12%' }}>
                                        Recipient
                                    </HideMobileCell>
                                    <TableCell sx={{ fontWeight: 600, width: '30%' }}>
                                        Email Content
                                    </TableCell>
                                    <TableCell align='center' sx={{ fontWeight: 600, width: '10%' }}>
                                        Flow
                                    </TableCell>
                                    <HideMobileCell align='right' sx={{ fontWeight: 600, width: '10%' }}>
                                        Date
                                    </HideMobileCell>
                                    <TableCell align='center' sx={{ fontWeight: 600, width: '8%' }}>
                                        Category
                                    </TableCell>
                                    <TableCell align='center' sx={{ fontWeight: 600, width: '8%' }}>
                                        Actions
                                    </TableCell>
                                </StyledTableRow>
                            </TableHead>
                            <TableBody>
                                {!emails || emails.emails.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center">
                                            <Box sx={{ py: 4 }}>
                                                <Email sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                                    No emails found
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {appliedEmailSearch || appliedFilters.creatorId || appliedFilters.category
                                                        ? 'Try adjusting your filters'
                                                        : 'Email history will appear here'}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    emails.emails.map((row) => {
                                        const isSelected = isEmailSelected(row.id);
                                        const replyCount = row.our_reply_count || 0;
                                        const threadPosition = row.thread_position || 1;
                                        const totalThreadMessages = row.total_thread_messages || 1;
                                        const emailStage = row.email_stage || 'received';
                                        
                                        return (
                                            <StyledTableRow 
                                                key={row.id}
                                                onClick={() => onEmailClick(row.id)} 
                                                sx={{ 
                                                    cursor: 'pointer',
                                                    opacity: row.admin_hidden ? 0.6 : 1,
                                                    backgroundColor: row.admin_hidden 
                                                        ? 'action.disabledBackground' 
                                                        : isSelected 
                                                            ? 'action.selected' 
                                                            : 'inherit',
                                                    '&:hover': {
                                                        backgroundColor: row.admin_hidden
                                                            ? 'action.disabledBackground'
                                                            : isSelected 
                                                                ? 'action.selected' 
                                                                : 'action.hover'
                                                    }
                                                }}
                                            >
                                                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onChange={(e) => handleSelectEmail(e, row.id)}
                                                    />
                                                </TableCell>
                                                <HideTabletCell>
                                                    <Chip 
                                                        label={row.creator_id} 
                                                        size="small" 
                                                        variant="outlined"
                                                    />
                                                </HideTabletCell>
                                                <TableCell>
                                                    <TruncatedText variant="body2">
                                                        {row.sender}
                                                    </TruncatedText>
                                                </TableCell>
                                                <HideMobileCell>
                                                    <TruncatedText variant="body2">
                                                        {row.receiver}
                                                    </TruncatedText>
                                                </HideMobileCell>
                                                <TableCell>
                                                    <Box>
                                                        <TruncatedText variant='body2' sx={{ fontWeight: 500 }}>
                                                            {row.subject}
                                                        </TruncatedText>
                                                        <TruncatedText variant='caption' color='text.secondary'>
                                                            {row.snippet}
                                                        </TruncatedText>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align='center'>
                                                    <Stack spacing={0.5} alignItems="center">
                                                        {totalThreadMessages > 1 && (
                                                            <Tooltip title={`Message ${threadPosition}/${totalThreadMessages}`}>
                                                                <Chip
                                                                    icon={<ThreadIcon sx={{ fontSize: 12 }} />}
                                                                    label={`${threadPosition}/${totalThreadMessages}`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                        
                                                        {replyCount > 0 && (
                                                            <Tooltip title={`${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}`}>
                                                                <Chip
                                                                    icon={<ReplyIcon sx={{ fontSize: 12 }} />}
                                                                    label={replyCount}
                                                                    size="small"
                                                                    color="success"
                                                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                        
                                                        <Chip
                                                            label={formatStage(emailStage).substring(0, 8)}
                                                            size="small"
                                                            color={STAGE_COLORS[emailStage]}
                                                            variant="outlined"
                                                            sx={{ height: 20, fontSize: '0.65rem' }}
                                                        />
                                                    </Stack>
                                                </TableCell>
                                                <HideMobileCell align='right'>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {new Date(row.date_received).toLocaleDateString()}
                                                    </Typography>
                                                    <Typography variant="caption" display="block" color="text.secondary">
                                                        {new Date(row.date_received).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </Typography>
                                                </HideMobileCell>
                                                <TableCell align='center'>
                                                    {row.category ? (
                                                        <Chip 
                                                            label={row.category} 
                                                            color={row.category === 'Paid' ? 'success' : row.category === 'Affiliate' ? 'secondary' : 'default'}
                                                            size="small"
                                                        />
                                                    ) : (
                                                        <Typography variant="caption" color="text.secondary">-</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align='center'>
                                                    <Tooltip title={row.admin_hidden ? "Restore" : "Archive"}>
                                                        <IconButton
                                                            size="small"
                                                            color={row.admin_hidden ? "primary" : "error"}
                                                            onClick={(e) => handleToggleEmailVisibility(e, row.id, row.admin_hidden)}
                                                            disabled={toggleHiddenMutation.isLoading}
                                                        >
                                                            {row.admin_hidden ? <RestoreFromTrash fontSize="small" /> : <Delete fontSize="small" />}
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </StyledTableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TablePagination
                                        colSpan={9}
                                        page={page}
                                        rowsPerPage={rowsPerPage}
                                        rowsPerPageOptions={[rowsPerPage]}
                                        count={emails?.total || 0}
                                        onPageChange={(event, newPage) => {
                                            setPage(newPage);
                                            setSelectedEmails([]);
                                            setSelectAll(false);
                                        }}
                                    />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </ResponsiveTableContainer>
                </Card>
            )}
        </Box>
    );
}